/**
 * The header's working bar, like an IDE's: the Git picker, ▶ Run, and the buttons installed plugins
 * put in the header.
 *
 * Git is the easiest thing to find: always here, the linked repositories listed by team, and a way
 * to connect another. Changing it changes what the whole app works on — the Explorer's files, ▶,
 * the audits — and everything that depends on it reloads.
 *
 * ▶ Executar sends the catalog's "run.docker" prompt, from the API or the desktop's cached copy, so
 * what it does can be reworded on the Prompts screen without a new release. The button then follows
 * the run (lib/repositoryRun.ts): a clock while it waits for an agent, ■ while an agent works (a
 * click stops it), and ▶ again when it ends — with what it started opened in the browser.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FolderGit2, Hourglass, Loader2, Play, Plus, Square } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { isDesktop, openExternal } from '../lib/desktop'
import { addressOf, notRunReason, useRepositoryRun } from '../lib/repositoryRun'
import { stopRunnerFor } from '../lib/runnerHost'
import type { WorkOrder } from '../lib/types'
import { useI18n } from '../lib/i18n'
import { useActiveRepository } from '../lib/activeRepository'
import { runPrompt, usePromptCatalog } from '../lib/prompts'
import type { RepositoryLink } from '../lib/types'
import { BranchPicker } from './BranchPicker'
import { ProjectActions } from './ProjectActions'
import { PublishMenu } from './PublishMenu'
import { RUN_ACTION_EVENT } from '../lib/composer'
import { followRepository } from '../lib/followProject'

/** `kids`: Kids mode (docs/refactor/48) — ▶ Executar and Analisar, nothing else. */
export function RunBar({ kids = false }: { kids?: boolean } = {}) {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const { linked, active, choose, loaded } = useActiveRepository()
  const { data: catalog } = usePromptCatalog()
  // Play is work for one's own agent: only a developer has one.
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })
  const [notice, setNotice] = useState<{ ok: boolean; text: string; open?: string; seeOrder?: boolean } | null>(null)
  const prompt = catalog?.prompts.find((p) => p.key === 'run.docker')
  const publishPrompt = catalog?.prompts.find((p) => p.key === 'run.publish')
  const { data: queue } = useQuery({ queryKey: ['prompt-queue'], queryFn: api.promptQueue.list, enabled: Boolean(me?.developerId), retry: false })

  // When the run ends: what it started, opened. The desktop's runner opens it on the machine that ran
  // it; a browser opens it only when no desktop app is running there too.
  const finished = useCallback(
    (order: WorkOrder) => {
      const address = addressOf(order)
      const notRun = notRunReason(order)
      if (order.status === 'Done' && notRun !== null && !address) {
        setNotice({ ok: false, seeOrder: true, text: notRun ? t('run.notRun', { reason: notRun }) : t('run.failedRun') })
      } else if (order.status === 'Done') {
        if (address && !isDesktop() && !queue?.desktopOnline) void openExternal(address)
        setNotice({ ok: true, text: address ? t('run.doneAt', { url: address }) : t('run.done'), open: address ?? undefined })
      } else {
        setNotice({ ok: false, seeOrder: true, text: order.status === 'Stopped' ? t('run.stopped') : t('run.failedRun') })
      }
    },
    [queue?.desktopOnline, t],
  )
  const { order, phase, follow } = useRepositoryRun(active?.id ?? null, finished)

  const run = useMutation({
    mutationFn: () => runPrompt(prompt!, active!.id, locale),
    onSuccess: (started) => {
      follow(started.id)
      setNotice({ ok: true, text: t('run.sent', { name: active!.name }) })
    },
    onError: (error) =>
      setNotice({ ok: false, text: error instanceof ApiError && error.code === 'remote_off' ? t('run.remoteOff') : t('run.failed') }),
  })
  const stop = useMutation({
    mutationFn: async () => {
      if (active) stopRunnerFor(active.name)
      if (order) await api.workOrders.stop(order.id)
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['run-order'] }),
  })

  // /executar in the prompt box: the same as clicking ▶ (lib/composer.ts).
  useEffect(() => {
    const onRun = () => {
      if (phase === 'idle' && prompt && active && !run.isPending) run.mutate()
    }
    window.addEventListener(RUN_ACTION_EVENT, onRun)
    return () => window.removeEventListener(RUN_ACTION_EVENT, onRun)
  }, [phase, prompt, active, run])

  if (!loaded) return null

  if (linked.length === 0) {
    return (
      <div className="run-bar" role="group" aria-label={t('run.label')}>
        <Link to="/agents/repositories" className="btn btn--sm git-connect">
          <FolderGit2 size={14} /> {t('git.connect')}
        </Link>
      </div>
    )
  }

  return (
    <div className="run-bar" role="group" aria-label={t('run.label')}>
      <div className="git-picker" title={t('git.pickerHint')}>
        <FolderGit2 size={14} aria-hidden />
        <select
          className="select select--sm run-bar__repo"
          value={active?.id ?? ''}
          onChange={(event) => {
            choose(event.target.value)
            setNotice(null)
            // Everything that reads the repository reloads for the new one.
            for (const key of ['repo-files', 'repo-branches', 'work-orders', 'teammates', 'prompt-queue']) {
              void queryClient.invalidateQueries({ queryKey: [key] })
            }
            // On the desktop, Trabalho goes to its folder — or the app asks where it is (docs/refactor/53).
            const next = linked.find((r) => r.id === event.target.value)
            if (next) void followRepository(next, queryClient)
          }}
          aria-label={t('run.repository')}
        >
          {/* Grouped by project: a project can hold several repositories. */}
          {groupByProject(linked).map(([project, repositories]) => (
            <optgroup key={project} label={project}>
              {repositories.map((repository) => (
                <option key={repository.id} value={repository.id}>
                  {repository.teamName ? `${repository.teamName} / ` : ''}
                  {repository.name}
                  {repository.agentListening ? ' ●' : ''}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        {!kids && <Link to="/agents/repositories" className="btn btn--ghost btn--icon btn--sm" aria-label={t('git.connect')} title={t('git.connect')}>
          <Plus size={13} />
        </Link>}
      </div>

      {/* The branch, always in sight; on the desktop, switched here as in VS Code. */}
      {active && !kids && <BranchPicker repository={active} />}

      {me?.developerId && prompt && (
        <button
          type="button"
          className={`btn btn--sm run-bar__play${phase === 'waiting' ? ' is-waiting' : ''}${phase === 'working' ? ' is-working' : ''}`}
          onClick={() => {
            setNotice(null)
            if (phase === 'idle') run.mutate()
            else stop.mutate()
          }}
          disabled={run.isPending || stop.isPending || !active}
          title={phase === 'working' ? t('run.stopHint') : phase === 'waiting' ? t('run.waitingHint') : t('run.playHint')}
          aria-label={phase === 'working' ? t('run.stop') : phase === 'waiting' ? t('run.waiting') : t('run.play')}
        >
          {run.isPending || stop.isPending ? (
            <Loader2 size={14} className="spin" />
          ) : phase === 'working' ? (
            <Square size={13} fill="currentColor" />
          ) : phase === 'waiting' ? (
            <Hourglass size={14} className="run-bar__hourglass" />
          ) : (
            <Play size={14} fill="currentColor" />
          )}
          <span>{phase === 'working' ? t('run.stop') : phase === 'waiting' ? t('run.waiting') : t('run.play')}</span>
        </button>
      )}

      {/* The arrow beside ▶: Executar or Publicar, a link to try the project away from home (docs/refactor/33). */}
      {me?.developerId && prompt && active && !kids && (
        <PublishMenu
          repository={active}
          prompt={publishPrompt}
          busy={run.isPending || phase !== 'idle'}
          onRun={() => {
            setNotice(null)
            run.mutate()
          }}
        />
      )}

      {/* Ações: Analisar, O que falta, Pendências and the plugins' buttons, in one menu beside ▶. */}
      {me?.developerId && active && <ProjectActions repository={active} only={kids ? 'analyze' : undefined} />}

      {notice && (
        <span className={`tiny run-bar__notice${notice.ok ? '' : ' run-bar__notice--bad'}`} role="status">
          {notice.text}{' '}
          {notice.open ? (
            <a href={notice.open} target="_blank" rel="noreferrer" onClick={(event) => {
              event.preventDefault()
              void openExternal(notice.open!)
            }}>
              {t('run.open')}
            </a>
          ) : (
            <Link to={notice.ok || notice.seeOrder ? '/agents/work' : '/agents/remote'} onClick={() => setNotice(null)}>
              {notice.ok || notice.seeOrder ? t('run.follow') : t('work.turnOnRemote')}
            </Link>
          )}
        </span>
      )}
    </div>
  )
}

/** Linked repositories by project (its key and name), projects in name order. */
function groupByProject(repositories: RepositoryLink[]): Array<[string, RepositoryLink[]]> {
  const groups = new Map<string, RepositoryLink[]>()
  for (const repository of repositories) {
    const label = repository.projectKey ? `${repository.projectKey} · ${repository.projectName ?? ''}`.trim() : '—'
    groups.set(label, [...(groups.get(label) ?? []), repository])
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b))
}
